import { useState, useMemo } from "react";
import { useNavigate } from "react-router";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import type { Workspace } from "@/lib/workspaces";
import { useWorkspacesStore } from "@/stores/workspaces-store";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from "@/components/ui/context-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getScreenshot } from "@/lib/workspace-screenshots";

const gradients = ["from-violet-600/80 to-indigo-800", "from-rose-600/80 to-pink-800", "from-amber-500/80 to-orange-800", "from-emerald-500/80 to-teal-800", "from-cyan-500/80 to-blue-800", "from-fuchsia-500/80 to-purple-800", "from-sky-500/80 to-indigo-800", "from-lime-500/80 to-green-800"];

function getGradient(id: string) {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return gradients[Math.abs(hash) % gradients.length];
}

function formatDate(ts: string) {
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function WorkspaceCard({ workspace }: { workspace: Workspace }) {
  const navigate = useNavigate();
  const { update, remove } = useWorkspacesStore();

  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [renameValue, setRenameValue] = useState(workspace.name);
  const [saving, setSaving] = useState(false);

  const initial = workspace.name.charAt(0).toUpperCase();
  const screenshot = useMemo(() => getScreenshot(workspace.id), [workspace.id]);

  function openRename() {
    setRenameValue(workspace.name);
    setRenameOpen(true);
  }

  async function handleRename() {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === workspace.name) {
      setRenameOpen(false);
      return;
    }
    setSaving(true);
    try {
      await update(workspace.id, { name: trimmed });
    } finally {
      setSaving(false);
      setRenameOpen(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await remove(workspace.id);
    } finally {
      setSaving(false);
      setDeleteOpen(false);
    }
  }

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <Card className="group cursor-pointer overflow-hidden p-0 text-left transition-all hover:border-muted-foreground/40 hover:shadow-lg hover:shadow-black/25" onClick={() => navigate(`/workspace/${workspace.id}`)}>
            {screenshot ? (
              <img src={screenshot} alt={workspace.name} className="aspect-[16/9] w-full object-cover" />
            ) : (
              <div className={`aspect-[16/9] w-full bg-gradient-to-br ${getGradient(workspace.id)} flex items-center justify-center`}>
                <span className="text-4xl font-bold text-white/20 select-none">{initial}</span>
              </div>
            )}
            <CardContent className="flex items-center gap-3 px-4 py-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-muted to-muted-foreground/20 text-xs font-semibold text-foreground">{initial}</div>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-sm font-medium text-foreground">{workspace.name}</h3>
                <p className="text-xs text-muted-foreground">Edited {formatDate(workspace.updatedAt)}</p>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 data-[state=open]:opacity-100" onClick={(e) => e.stopPropagation()}>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenuItem onClick={openRename}>
                    <Pencil className="h-4 w-4" />
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </CardContent>
          </Card>
        </ContextMenuTrigger>

        <ContextMenuContent>
          <ContextMenuItem onClick={openRename}>
            <Pencil className="h-4 w-4" />
            Rename
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem className="text-destructive focus:bg-destructive/10 focus:text-destructive" onClick={() => setDeleteOpen(true)}>
            <Trash2 className="h-4 w-4" />
            Delete
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      {/* Rename Dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename workspace</DialogTitle>
            <DialogDescription>Enter a new name for this workspace.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleRename();
            }}
          >
            <div className="grid gap-2 py-4">
              <Label htmlFor="workspace-name">Name</Label>
              <Input id="workspace-name" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} autoFocus />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenameOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving || !renameValue.trim()}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete workspace?</AlertDialogTitle>
            <AlertDialogDescription>This removes the workspace from n8nvibes and disables its proxy URL. It does not delete anything inside your n8n instance.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={saving} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {saving ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
